import { useState, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Play,
  Loader2,
  Type,
  Hash,
  ToggleLeft,
  List,
  Calendar,
  CheckSquare,
  Square,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSources } from '@/features/sources/hooks/useSources';
import { useRunBatchExtraction } from '@/features/export/hooks/useExport';
import type { ExtractionField } from '@/features/export/hooks/useExport';

// =========================================
// DataExtractionRunner — schema builder + source picker + run
// =========================================

const FIELD_TYPE_ICONS: Record<ExtractionField['type'], typeof Type> = {
  text: Type,
  number: Hash,
  boolean: ToggleLeft,
  list: List,
  date: Calendar,
};

const DEFAULT_SCHEMA: ExtractionField[] = [
  { name: 'title', type: 'text', description: 'Title of the study', required: true },
  { name: 'sample_size', type: 'number', description: 'Number of participants', required: false },
  { name: 'has_control_group', type: 'boolean', description: 'Whether study has a control group', required: false },
  { name: 'publication_year', type: 'text', description: 'Year of publication', required: false },
  { name: 'keywords', type: 'list', description: 'Main keywords or topics', required: false },
];

interface DataExtractionRunnerProps {
  onExtractionComplete?: () => void;
  className?: string;
}

export function DataExtractionRunner({
  onExtractionComplete,
  className,
}: DataExtractionRunnerProps) {
  const [schema, setSchema] = useState<ExtractionField[]>(DEFAULT_SCHEMA);
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<ExtractionField['type']>('text');
  const [newFieldDesc, setNewFieldDesc] = useState('');

  const { data: sources } = useSources();
  const batchExtraction = useRunBatchExtraction();

  // Only sources with extracted text
  const extractedSources = (sources ?? []).filter(
    (s: { extraction_status?: string }) => s.extraction_status === 'completed'
  );

  const toggleSource = useCallback((id: string) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllSources = useCallback(() => {
    if (selectedSourceIds.size === extractedSources.length) {
      setSelectedSourceIds(new Set());
    } else {
      setSelectedSourceIds(new Set(extractedSources.map((s: { id: string }) => s.id)));
    }
  }, [extractedSources, selectedSourceIds.size]);

  const addField = useCallback(() => {
    if (!newFieldName.trim()) return;
    if (schema.some((f) => f.name === newFieldName.trim())) return;
    setSchema((prev) => [
      ...prev,
      {
        name: newFieldName.trim(),
        type: newFieldType,
        description: newFieldDesc.trim() || newFieldName.trim(),
        required: false,
      },
    ]);
    setNewFieldName('');
    setNewFieldDesc('');
  }, [newFieldName, newFieldType, newFieldDesc, schema]);

  const removeField = useCallback((name: string) => {
    setSchema((prev) => prev.filter((f) => f.name !== name));
  }, []);

  const handleRun = useCallback(async () => {
    if (selectedSourceIds.size === 0 || schema.length === 0) return;
    await batchExtraction.mutateAsync({
      source_ids: Array.from(selectedSourceIds),
      schema,
    });
    onExtractionComplete?.();
  }, [selectedSourceIds, schema, batchExtraction, onExtractionComplete]);

  const creditsNeeded = selectedSourceIds.size;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Schema Builder */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Extraction Schema</CardTitle>
            <Badge variant="outline" className="text-xs">
              {schema.length} fields
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Define the fields to extract from each source using AI.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Current fields */}
          <div className="space-y-1.5">
            {schema.map((field) => {
              const Icon = FIELD_TYPE_ICONS[field.type] ?? Type;
              return (
                <div
                  key={field.name}
                  className="flex items-center gap-2 py-1.5 px-2.5 rounded-md bg-slate-50 border text-sm"
                >
                  <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="font-medium text-slate-700">{field.name}</span>
                  <Badge variant="outline" className="text-[10px] px-1">
                    {field.type}
                  </Badge>
                  <span className="text-xs text-slate-400 truncate flex-1">
                    {field.description}
                  </span>
                  {field.required && (
                    <Badge className="text-[10px] px-1 bg-amber-100 text-amber-700 border-amber-200">
                      req
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-400 hover:text-rose-600 shrink-0"
                    onClick={() => removeField(field.name)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Add new field */}
          <div className="flex items-end gap-2 pt-2 border-t">
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-slate-500">Field name</Label>
              <Input
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="e.g. methodology"
                className="h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addField()}
              />
            </div>
            <div className="w-28 space-y-1">
              <Label className="text-xs text-slate-500">Type</Label>
              <Select
                value={newFieldType}
                onValueChange={(v) => setNewFieldType(v as ExtractionField['type'])}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="list">List</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-slate-500">Description</Label>
              <Input
                value={newFieldDesc}
                onChange={(e) => setNewFieldDesc(e.target.value)}
                placeholder="What to extract"
                className="h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addField()}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={addField}
              disabled={!newFieldName.trim()}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Source Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Select Sources</CardTitle>
              <Badge variant="outline" className="text-xs">
                {selectedSourceIds.size} / {extractedSources.length} selected
              </Badge>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllSources}>
              {selectedSourceIds.size === extractedSources.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Only sources with extracted text are available.
          </p>
        </CardHeader>
        <CardContent>
          {extractedSources.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No sources with extracted text found. Upload and extract text first.
            </p>
          ) : (
            <div className="grid gap-1.5 max-h-48 overflow-y-auto">
              {extractedSources.map((source: { id: string; title?: string | null; file_name: string }) => {
                const selected = selectedSourceIds.has(source.id);
                return (
                  <button
                    key={source.id}
                    type="button"
                    className={cn(
                      'flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-left transition-colors',
                      selected
                        ? 'bg-indigo-50 border border-indigo-200 text-indigo-700'
                        : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                    )}
                    onClick={() => toggleSource(source.id)}
                  >
                    {selected ? (
                      <CheckSquare className="h-4 w-4 text-indigo-500 shrink-0" />
                    ) : (
                      <Square className="h-4 w-4 text-slate-300 shrink-0" />
                    )}
                    <span className="truncate">
                      {source.title || source.file_name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run Button */}
      <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50">
        <div className="text-sm text-slate-600">
          <span className="font-medium">{selectedSourceIds.size}</span> sources
          {' × '}
          <span className="font-medium">{schema.length}</span> fields
          {' = '}
          <span className="font-semibold text-indigo-600">{creditsNeeded} credits</span>
        </div>
        <Button
          onClick={handleRun}
          disabled={
            selectedSourceIds.size === 0 ||
            schema.length === 0 ||
            batchExtraction.isPending
          }
          className="gap-2"
        >
          {batchExtraction.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Extracting...
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Run Extraction ({creditsNeeded} credits)
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
